/**
 * Global notification context for toast messages using Sonner
 */
import { createContext, useContext, useMemo, useEffect } from 'react';
import { toast } from 'sonner';

type SeverityType = 'info' | 'success' | 'warning' | 'error';

interface NotificationContextType {
  /**
   * `description` is a second line under the message, for the detail that makes an
   * alert worth reading — the note on a due reminder, the excerpt on a mention.
   * Truncate it at the CALL SITE: a toast is a glance, and sonner will happily
   * grow to hold a 500-character paragraph.
   */
  showNotification: (message: string, severity?: SeverityType, description?: string) => void;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string, description?: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const showNotification = (
    message: string,
    severity: SeverityType = 'info',
    description?: string,
  ) => {
    // `undefined` rather than an omitted key: sonner reads `description` off the
    // options object and renders no second line for undefined, so one call shape
    // serves both.
    const options = { description };
    switch (severity) {
      case 'error':
        toast.error(message, options);
        break;
      case 'success':
        toast.success(message, options);
        break;
      case 'warning':
        toast.warning(message, options);
        break;
      case 'info':
      default:
        toast.info(message, options);
        break;
    }
  };

  const showError = (message: string) => showNotification(message, 'error');
  const showSuccess = (message: string) => showNotification(message, 'success');
  const showWarning = (message: string) => showNotification(message, 'warning');
  const showInfo = (message: string, description?: string) =>
    showNotification(message, 'info', description);

  // Listen for global mutation error events from QueryClient
  useEffect(() => {
    const handleMutationError = (
      event: CustomEvent<{ message: string; level?: 'error' | 'info' }>,
    ) => {
      // `level` is optional so an older dispatcher (or a test firing the raw event)
      // still lands on the error toast it always did. `info` is for a 4xx that
      // reports an OUTCOME — see OUTCOME_NOT_FAILURE_CODES in globalErrorToasts.
      if (event.detail.level === 'info') showInfo(event.detail.message);
      else showError(event.detail.message);
    };

    window.addEventListener('mutation-error', handleMutationError as EventListener);
    return () => {
      window.removeEventListener('mutation-error', handleMutationError as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- showError/showInfo are stable
  }, []);

  const value = useMemo<NotificationContextType>(
    () => ({
      showNotification,
      showError,
      showSuccess,
      showWarning,
      showInfo,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- functions are stable
    []
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification(): NotificationContextType {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}
